import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// ═══════════════════════════════════════════════════════════════════════════
// La campanita del salón
//
// La tabla `notifications` existe desde el primer día del proyecto y **nadie
// escribía ni leía nada en ella**. El icono de la campana en la barra
// superior era un botón sin `onClick`: se podía pulsar y no pasaba nada.
//
// Es el patrón de siempre —`Patrón — Construido a medias y nunca abierto`—
// solo que aquí faltaban las DOS puntas: ni quien escribe ni quien lee.
// ═══════════════════════════════════════════════════════════════════════════

export interface AvisoDelSalon {
  id: string;
  templateKey: string;
  payload: {
    requestId?: string;
    petName?: string;
    customerName?: string;
    serviceName?: string;
    preferredStart?: string;
    depositCents?: number;
    link?: string;
    // Facturación electrónica (fe_rechazada, fe_cupo_*, fe_*_por_vencer).
    documentoId?: string;
    tipo?: string;
    numero?: string | number;
    mensaje?: string;
    usados?: number;
    limite?: number;
    prefijo?: string;
    validoHasta?: string;
    expiraEn?: string;
    dias?: number;
    /** Solo en los avisos al superadministrador: de qué salón se trata. */
    tenantName?: string;
  };
  readAt: string | null;
  createdAt: string;
}

export const avisoKeys = {
  todos: ['notificaciones'] as const,
};

export function useAvisosDelSalon() {
  return useQuery({
    queryKey: avisoKeys.todos,
    queryFn: async (): Promise<AvisoDelSalon[]> => {
      const { data, error } = await supabase.rpc('staff_notificaciones', {
        p_solo_no_leidas: false,
      });
      if (error) throw error;
      return (data as AvisoDelSalon[] | null) ?? [];
    },
    // Quien tiene el sistema abierto todo el día no debería tener que
    // recargar para enterarse de que entró una reserva.
    refetchInterval: 120_000,
    // Un fallo aquí no puede romper la pantalla: es un accesorio.
    retry: 1,
  });
}

/**
 * Cuántos avisos sin leer tiene quien mira, contados en la base.
 *
 * `staff_notificaciones` devuelve los 50 más recientes —un tope legítimo para
 * la lista del popover— y el contador se sacaba filtrando esa lista: nunca
 * pasaba de 50, y los no leídos más antiguos no contaban. En CalzadoPro un
 * usuario tenía 594 sin leer y la campana decía otra cosa. Aquí se cuenta con
 * `count: 'exact'` y `head: true`, que no baja ninguna fila.
 *
 * Mismo criterio que la función: avisos de la app (IN_APP) de este usuario;
 * la empresa la filtra RLS (`tenant_id = current_tenant_id()`). El usuario se
 * filtra a mano porque la política deja ver los de otros a quien tiene
 * `notification:read`.
 *
 * Devuelve `null` si no se pudo contar: la campana usa entonces lo que trae
 * la lista, que es lo que hacía antes.
 */
export function useAvisosSinLeer() {
  return useQuery({
    queryKey: [...avisoKeys.todos, 'sin-leer'] as const,
    queryFn: async (): Promise<number | null> => {
      const { data: sesion } = await supabase.auth.getSession();
      const uid = sesion.session?.user.id;
      if (!uid) return null;
      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', uid)
        .eq('channel', 'IN_APP')
        .is('read_at', null);
      if (error) return null;
      return count ?? null;
    },
    refetchInterval: 120_000,
    retry: 1,
  });
}

export function useMarcarAvisosLeidos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids?: string[]) => {
      const { data, error } = await supabase.rpc('staff_notificaciones_marcar_leidas', {
        p_ids: ids ?? null,
      });
      if (error) throw error;
      return data as number;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: avisoKeys.todos }),
  });
}
